/* eslint-disable no-nested-ternary */
/* eslint-disable radix */
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "../../../../Core/Components/Modal/CustomModal";
import CustomRoundedImage from "../../../../Core/Components/CustomRoundedImage";
import { TrashSecondaryIcon } from "../../../../Common/Svgs/TrashIcon";
import CloseIcon from "../../../../Common/Svgs/CloseIcon";
import Button from "../../../../Core/Components/CustomButton";
import UserIcon from "../../../../Common/Svgs/UserIcon";
import {
  deleteDriverV3,
  getDriversV3,
  reAssignAndDelete,
} from "../../../../Redux/APIs/usersAPI";
import { updateUsers } from "../../../../Redux/Slices/users.slice";
import TransferTripDropdown from "./TransferTripDropdown";

const LOCAL_DEFAULT_LIMIT = 10;

const DeleteDriverModal = ({
  visible,
  onCancel,
  driversCurrentPage,
  setDriversCurrentPage,
}) => {
  const dispatch = useDispatch();

  const { loading, deleteDriverModal, currentDriver, driverUpcomingTrips } =
    useSelector((state) => state.users);

  const [transferToDriver, setTransferToDriver] = useState({
    id: 0,
    firstName: "",
    lastName: "",
  });

  const handleCancel = () => {
    setTransferToDriver({ id: 0, firstName: "", lastName: "" });
    onCancel();
  };

  const onConfirm = async () => {
    await dispatch(deleteDriverV3({ driverId: deleteDriverModal.id }));

    const response = await dispatch(
      getDriversV3({
        offset: ((driversCurrentPage || 1) - 1) * LOCAL_DEFAULT_LIMIT,
      }),
    );

    // If no records exist for the current page, move to the previous page
    if (!response?.payload?.result?.length && driversCurrentPage > 1) {
      const previousPage = driversCurrentPage - 1;
      setDriversCurrentPage(previousPage);

      await dispatch(
        getDriversV3({
          offset: (previousPage - 1) * LOCAL_DEFAULT_LIMIT,
        }),
      );
    }

    await dispatch(
      updateUsers({
        key: "showToastMessage",
        value: {
          visible: true,
          data: {
            title: "1 Driver Deleted",
            description: `${
              deleteDriverModal?.name || "1 driver"
            } was deleted sucessfully.`,
          },
        },
      }),
    );

    handleCancel();
  };

  const onReassignAndDelete = async () => {
    if (transferToDriver.id === 0) return;

    await dispatch(
      reAssignAndDelete({
        fromDriverId: Number(deleteDriverModal.id),
        toDriverId: Number(transferToDriver.id),
      }),
    );

    const response = await dispatch(
      getDriversV3({
        offset: ((driversCurrentPage || 1) - 1) * LOCAL_DEFAULT_LIMIT,
      }),
    );

    // If no records exist for the current page, move to the previous page
    if (!response?.payload?.result?.length && driversCurrentPage > 1) {
      const previousPage = driversCurrentPage - 1;
      setDriversCurrentPage(previousPage);

      await dispatch(
        getDriversV3({
          offset: (previousPage - 1) * LOCAL_DEFAULT_LIMIT,
        }),
      );
    }

    await dispatch(
      updateUsers({
        key: "showToastMessage",
        value: {
          visible: true,
          data: {
            title: "1 Driver Deleted",
            description: `${
              deleteDriverModal?.name || "1 driver"
            } was deleted sucessfully.`,
          },
        },
      }),
    );

    handleCancel();
  };

  const getBody = () => {
    if (currentDriver?.currentTrike) {
      return (
        <div className="px-2 mt-5">
          <p className="text-base font-medium text-alert-red font-poppins">
            Cannot delete driver
          </p>
          <p className="mt-2 text-xs font-normal text-black font-aileron">
            The driver is currently on a trip.
          </p>
        </div>
      );
    }

    if (driverUpcomingTrips?.length) {
      return (
        <div className="px-2 mt-5">
          <p className="text-base font-medium text-alert-red font-poppins">
            Delete driver
          </p>

          <p className="mt-2 text-xs font-normal text-black font-aileron">
            Deletion is not possible at this time.
          </p>

          <p className="mt-2 mb-4 text-xs font-normal text-black font-aileron">
            This driver is assigned to{" "}
            <span className="font-semibold">
              {driverUpcomingTrips?.length} upcoming trips.
            </span>{" "}
            Please re-assign the trips to continue.
          </p>

          <TransferTripDropdown
            transferToDriver={transferToDriver}
            setTransferToDriver={setTransferToDriver}
          />
        </div>
      );
    }

    return (
      <div className="px-2 mt-5">
        <p className="text-base font-medium text-alert-red font-poppins">
          Delete driver
        </p>
        <p className="mt-2 text-xs font-normal text-black font-aileron">
          Are you sure you want to delete this driver from the system?
        </p>
        <p className="mt-2 text-xs font-normal text-black font-aileron">
          This action cannot be undone.
        </p>
      </div>
    );
  };

  const getConfirmButton = () => {
    if (currentDriver?.currentTrike) return null;

    if (driverUpcomingTrips?.length) {
      return (
        <button
          type="button"
          disabled={loading || transferToDriver?.id === 0}
          className={`px-4 py-3 h-10.5 text-white rounded-md
            ${
              transferToDriver?.id === 0
                ? "bg-[#B1B1B1] cursor-not-allowed"
                : loading
                ? "bg-alert-red-hover cursor-not-allowed"
                : "bg-alert-red hover:bg-alert-red-hover cursor-pointer"
            }
            `}
          onClick={onReassignAndDelete}
        >
          <p className="font-poppins text-xs font-medium capitalize">
            Re-Assign & Delete
          </p>
        </button>
      );
    }

    return (
      <Button colors="alert" size="primary" onClick={onConfirm}>
        Confirm
      </Button>
    );
  };

  return (
    <CustomModal
      visible={visible}
      onCancel={handleCancel}
      width="536px"
      centered
    >
      <div className="w-full p-4 rounded-md bg-background-white">
        <div className="flex items-center justify-between">
          <div className="relative">
            <div className="absolute top-0 right-0">
              <TrashSecondaryIcon className="text-alert-red" />
            </div>
            <CustomRoundedImage
              size="w-12 h-12"
              image={<UserIcon className="stroke-white fill-none" />}
              background="bg-primary"
            />
          </div>
          <button type="button" onClick={handleCancel}>
            <CloseIcon className="w-6 h-6 stroke-dark-gray" />
          </button>
        </div>

        {getBody()}

        <div className="flex justify-end gap-x-3 px-2 mt-5.5">
          <Button colors="cancelButton" size="primary" onClick={onCancel}>
            Cancel
          </Button>

          {/* {!currentDriver?.currentTrike && (
            <Button colors="alert" size="primary" onClick={onConfirm}>
              Confirm
            </Button>
          )} */}

          {getConfirmButton()}
        </div>
      </div>
    </CustomModal>
  );
};

export default DeleteDriverModal;
